import React, { Component } from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import uniq from 'lodash/uniq';

import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { ensureOwnListing } from '../../util/data';
import { axiosApi, handleAxiosResponse } from '../../util/axiosApi';
import { getPostsKeywords, getDescriptionKeywords } from '../../util/editListing';

import { EditListingPostsForm } from '../../forms';
import { ListingLink } from '../../components';

import css from './EditListingPostsPanel.module.css';

class EditListingPostsPanel extends Component {
  constructor(props) {
    super(props);

    this.state = {
      news: this.getNews(props),
      content: this.getContent(props),
      events: this.getEvents(props),
      getPreviewInProgress: false,
      latesNewsAddedDate: null
    };
  }

  setNews = news => {
    this.setState({ news });
  };
  setContent = content => {
    this.setState({ content });
  };
  setEvents = events => {
    this.setState({ events });
  };

  getNews(props) {
    return get(props, 'listing.attributes.publicData.news', []);
  }

  getContent(props) {
    return get(props, 'listing.attributes.publicData.content', []);
  }

  getEvents(props) {

    // let events = get(props, 'listing.attributes.publicData.events', []);
    // !!events?.[0] && events.map(e => {
    //   e.date = {date: new Date(e?.date) || null}
    // })
    return get(props, 'listing.attributes.publicData.events', []);;
  }

  setLatesNewsAddedDate = () => {
    this.setState({
      latesNewsAddedDate: Date.now()
    });
  }

  render() {
    const {
      className,
      rootClassName,
      errors,
      disabled,
      ready,
      listing,
      submitButtonText,
      panelUpdated,
      updateInProgress,
      onChange,
      onSubmit,
      currentUser,
      onUpdateProfile,
      isDraftListing,
      onHandlePreviewPopup
    } = this.props;

    const rootClass = rootClassName || css.root;
    const classes = classNames(rootClass, className);
    const currentListing = ensureOwnListing(listing);
    const isClient = currentUser?.attributes?.profile?.publicData?.userType === 'client';

    const isPublished =
      currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
    const panelTitle = isPublished ? (
      <FormattedMessage
        id="EditListingPostsPanel.title"
        values={{
          listingTitle: (
            <ListingLink listing={listing}>
              <FormattedMessage id="EditListingPostsPanel.listingTitle" />
            </ListingLink>
          ),
        }}
      />
    ) : (
      <FormattedMessage id="EditListingPostsPanel.createListingTitle" />
    );

    const publicData = get(currentListing, 'attributes.publicData', null);

    return (
      <div className={classes}>
        <h1 className={css.title}>
          {panelTitle}
        </h1>
        <EditListingPostsForm
          className={css.form}
          disabled={disabled}
          ready={ready}
          fetchErrors={errors}
          initialValues={{
            news: this.state.news,
            content: this.state.content,
            events: this.state.events,
          }}
          onSetNews={this.setNews}
          onSetContent={this.setContent}
          onSetEvents={this.setEvents}
          onSubmit={async values => {
            let { news, content, events, ...updateValues } = values;

            const newsData = !!news?.[0] ? news : [];
            const contentData = !!content?.[0] ? content : [];
            const eventsData = !!events?.[0] ? events : [];

            const keywords = uniq([
              ...getPostsKeywords(values.news, values.content, values.events),
              ...getDescriptionKeywords(
                get(currentListing, 'attributes.title', ''),
                get(currentListing, 'attributes.description', ''),
                get(publicData, 'category', []),
                get(publicData, 'subcategory', []),
                get(publicData, 'differentiationFromCompetitors', []),
              ),
            ]);

            const finalValues = {
              publicData: {
                keywords,
                news: !!news?.length ? news.sort((a, b) => b.id - a.id) : null,
                content: !!content?.length ? content.sort((a, b) => b.id - a.id) : null,
                events: !!events?.length ? events.filter(e => !!e.eventDate).sort((a, b) => b.id - a.id) : null,
                latesNewsAddedDate: !!this.state.latesNewsAddedDate ? this.state.latesNewsAddedDate : null,
                hasAnyNews:
                  (!!news && !!news.filter(n => !!n?.title && n?.id)?.length) ||
                  (!!content && !!content.filter(c => !!c?.title && c?.id)?.length) ||
                  (!!events && !!events.filter(e => !!e?.title && e?.eventDate)?.length) ||
                  null,
              },
              ...updateValues,
            };

            const getLinkPreview = async (postsArray) => {
              for (let i = 0; i < postsArray.length; i++) {
                const item = postsArray[i];
                if (!item?.link) {
                  item.image = {
                    url: null,
                  };
                } else {
                  await axiosApi.get(`/link-preview?link=${item.link}`)
                    .then(handleAxiosResponse)
                    .then(data => {
                      const image = get(data, 'imgUrl', null);
                      item.image = {
                        url: image,
                      };
                    })
                    .catch(e => {
                      item.image = {
                        url: null,
                      };
                    })
                    ;
                }
              }
            };

            this.setState({
              getPreviewInProgress: true,
            });

            await getLinkPreview(newsData);
            await getLinkPreview(contentData);
            await getLinkPreview(eventsData);

            this.setState({
              getPreviewInProgress: false,
            });

            return onSubmit(finalValues)
          }}
          onChange={onChange}
          saveActionMsg={submitButtonText}
          updated={panelUpdated}
          updateInProgress={updateInProgress || this.state.getPreviewInProgress}
          isClient={isClient}
          isDraftListing={isDraftListing}
          listing={currentListing}
          setLatesNewsAddedDate={this.setLatesNewsAddedDate}
          onHandlePreviewPopup={onHandlePreviewPopup}
        />
      </div>
    );
  }
}

EditListingPostsPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  images: [],
  listing: null,
};

EditListingPostsPanel.propTypes = {
  className: string,
  rootClassName: string,
  errors: object,
  disabled: bool.isRequired,
  ready: bool.isRequired,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
};

export default EditListingPostsPanel;
